import styled from '@emotion/styled'
import { PostPricingReportByTypePdfDto } from '@pharmac-extranet/extranet-api'
import {
  ControlledDateRangePicker,
  ControlledSelect,
  ExtranetContainer,
  ExtranetModal,
  ExtranetModalHeader,
  useBreakpoint,
  useLanguage,
} from '@pharmac-extranet/shared'
import {
  PharmacButton,
  PharmacH4,
  PharmacIcon,
  PharmacLabel,
  PharmacLoadingDots,
  PharmacText,
  PharmacTooltip,
} from '@pharmac/ui'
import { useMutation } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { Control, FieldValues, FormProvider, useForm } from 'react-hook-form'
import { ExtranetWrapper } from '../../components/extranet/ExtranetWrapper'
import { useCustomerContext } from '../../features/customer/CustomerProvider'
import { api } from '../../services/HttpService'

const ReportOptionsContainer = styled.div`
  background: var(--tabs-content-background);
  overflow: hidden;
  border-radius: 20px 20px 20px 20px;
  width: 100%;
  border: 1px solid #d9d9dd;
  overflow: visible;
`
const LoadingModalLabel = styled(PharmacLabel)`
  text-align: center;
`

type ReportLoadingModalProps = {
  showOnProp?: boolean
}

export const CustomerReportsView = () => {
  const { t } = useLanguage(['extranet'])
  const { minDesktop } = useBreakpoint()

  const ReportLoadingModal = ({ showOnProp }: ReportLoadingModalProps) => {
    const title = t('customer.reports.modalTitle')

    return (
      <ExtranetModal
        showOnProp={showOnProp}
        preventClose={true}
        modalBackground={'rgba(0, 0, 0, 0.13)'}
        style={{
          position: minDesktop ? 'inherit' : 'fixed',
          top: minDesktop ? '' : '6rem',
          maxHeight: '180px',
          maxWidth: 'none',
          width: minDesktop ? 'fit-content' : '97%',
        }}
      >
        <ExtranetModalHeader>
          <LoadingModalLabel>{title}</LoadingModalLabel>
        </ExtranetModalHeader>

        <ExtranetContainer
          justifyCenter
          marginSmallBottom
          marginSmallTop
        >
          <PharmacLoadingDots />
        </ExtranetContainer>
      </ExtranetModal>
    )
  }

  const ReportsOptions = () => {
    const { minDesktop, maxMobile } = useBreakpoint()
    const { customer } = useCustomerContext()

    const formhook = useForm<PostPricingReportByTypePdfDto>({
      mode: 'onChange',
      defaultValues: {
        reportType: undefined,
        reportStartDate: '',
        reportEndDate: '',
      },
    })

    const { mutate: getReportData, isPending: reportPending } = useMutation({
      mutationFn: async (data: any) =>
        await api.customers.getPricingReportByTypeAndDate(customer!.id, data, { format: 'blob' }),
      onSuccess: (res) => {
        window.open(window.URL.createObjectURL(res), 'blank')
      },
    })

    const isValidDateRange = () => {
      const start = new Date(formhook.getValues('reportStartDate'))
      const end = new Date(formhook.getValues('reportEndDate'))

      if (!start || !end) {
        return false
      }

      const diffMs = end.getTime() - start.getTime()
      const diffDays = diffMs / (1000 * 60 * 60 * 24)
      return diffDays <= 30
    }

    const useMobileDateRangePickerWidth = () => {
      const ref = useRef<HTMLDivElement | null>(null)
      const [width, setWidth] = useState(0)

      useEffect(() => {
        if (!ref.current) return

        const ro = new ResizeObserver(([entry]) => {
          setWidth(entry.contentRect.width)
        })

        ro.observe(ref.current)

        return () => ro.disconnect()
      }, [])

      return { width: width.toString(), ref }
    }

    const { ref, width } = useMobileDateRangePickerWidth()

    return (
      <>
        <FormProvider {...formhook}>
          {minDesktop ? (
            <ExtranetContainer
              marginLarge
              alignEnd
            >
              <ExtranetContainer
                half
                flexColumn
              >
                <ExtranetContainer>
                  <PharmacLabel marginSmallRight>{t('customer.reports.reportType')}</PharmacLabel>
                  <PharmacTooltip
                    handle={
                      <PharmacIcon
                        icon="info"
                        style={{
                          color: 'var(--color-green)',
                          marginRight: 'var(--spacing-tiny)',
                        }}
                      />
                    }
                  >
                    {t('customer.reports.reportTypeTooltip')}
                  </PharmacTooltip>
                </ExtranetContainer>
                <div style={{ width: '90%' }}>
                  <ControlledSelect
                    control={formhook.control as unknown as Control<FieldValues>}
                    name="reportType"
                    rules={{ required: true, validate: (value) => value != '' }}
                  >
                    <option
                      key={0}
                      value=""
                    >
                      {' '}
                      -{' '}
                    </option>
                    <option
                      key={1}
                      value="purchaseReport"
                    >
                      {t('customer.reports.purchaseReport')}
                    </option>
                    {/* 
                  disabled until a faster and better pdf generation is implemented in erp
                  <option
                    key={1}
                    value="productBreakdown"
                  >
                    {t('customer.reports.productBreakdown')}
                  </option>
                  */}

                    <option
                      key={2}
                      value="customerGroupBreakdown"
                    >
                      {t('customer.reports.customerGroupBreakdown')}
                    </option>
                  </ControlledSelect>
                </div>
              </ExtranetContainer>

              <ExtranetContainer
                fourth
                flexColumn
              >
                <ExtranetContainer>
                  <PharmacLabel marginSmallRight>{t('customer.reports.timeInterval')}</PharmacLabel>
                  <PharmacTooltip
                    handle={
                      <PharmacIcon
                        icon="info"
                        style={{
                          color: 'var(--color-green)',
                          marginRight: 'var(--spacing-tiny)',
                        }}
                      />
                    }
                  >
                    {t('customer.reports.timeIntervalTooltip')}
                  </PharmacTooltip>
                </ExtranetContainer>

                <ControlledDateRangePicker
                  control={formhook.control as unknown as Control<FieldValues>}
                  startDateName={'reportStartDate'}
                  endDateName={'reportEndDate'}
                  startDateText={t('startDateText', { ns: 'common' })}
                  endDateText={t('endDateText', { ns: 'common' })}
                  placeholder={t('selectDateRange', { ns: 'common' })}
                  weekText={t('week', { ns: 'common' })}
                  calendarPlacement="bottom"
                />
              </ExtranetContainer>

              <ExtranetContainer fourth>
                <PharmacButton
                  variant="secondary"
                  disabled={!formhook.formState.isValid || !isValidDateRange()}
                  onClick={formhook.handleSubmit(() => getReportData(formhook.getValues()))}
                >
                  <PharmacIcon icon="fileText" />
                  <PharmacText marginSmallLeft>{t('customer.reports.formReport')}</PharmacText>
                </PharmacButton>
              </ExtranetContainer>
            </ExtranetContainer>
          ) : (
            // MOBILE
            <ExtranetContainer
              marginLarge
              flexColumn
            >
              <ExtranetContainer
                full
                flexColumn
              >
                <ExtranetContainer>
                  <PharmacLabel marginSmallRight>{t('customer.reports.reportType')}</PharmacLabel>
                  <PharmacTooltip
                    maxWidth={maxMobile ? '295px' : ''}
                    handle={
                      <PharmacIcon
                        icon="info"
                        style={{
                          color: 'var(--color-green)',
                          marginRight: 'var(--spacing-tiny)',
                        }}
                      />
                    }
                  >
                    {t('customer.reports.reportTypeTooltip')}
                  </PharmacTooltip>
                </ExtranetContainer>
                <div style={{ width: '100%' }}>
                  <ControlledSelect
                    control={formhook.control as unknown as Control<FieldValues>}
                    name="reportType"
                    rules={{ required: true, validate: (value) => value != '' }}
                  >
                    <option
                      key={0}
                      value=""
                    >
                      -
                    </option>
                    <option
                      key={1}
                      value="purchaseReport"
                    >
                      {t('customer.reports.purchaseReport')}
                    </option>
                    {/*                 
                  disabled until a faster and better pdf generation is implemented in erp
                  <option
                    key={3}
                    value="productBreakdown"
                  >
                    {t('customer.reports.productBreakdown')}
                  </option>
                  */}
                    <option
                      key={2}
                      value="customerGroupBreakdown"
                    >
                      {t('customer.reports.customerGroupBreakdown')}
                    </option>
                  </ControlledSelect>
                </div>
              </ExtranetContainer>

              <ExtranetContainer
                full
                flexColumn
                marginMediumTop
              >
                <ExtranetContainer>
                  <PharmacLabel marginSmallRight>{t('customer.reports.timeInterval')}</PharmacLabel>
                  <PharmacTooltip
                    maxWidth={maxMobile ? '295px' : ''}
                    handle={
                      <PharmacIcon
                        icon="info"
                        style={{
                          color: 'var(--color-green)',
                          marginRight: 'var(--spacing-tiny)',
                        }}
                      />
                    }
                  >
                    {t('customer.reports.timeIntervalTooltip')}
                  </PharmacTooltip>
                </ExtranetContainer>

                <div
                  ref={ref}
                  style={{ width: '100%' }}
                >
                  <ControlledDateRangePicker
                    control={formhook.control as unknown as Control<FieldValues>}
                    startDateName={'reportStartDate'}
                    endDateName={'reportEndDate'}
                    startDateText={t('startDateText', { ns: 'common' })}
                    endDateText={t('endDateText', { ns: 'common' })}
                    placeholder={t('selectDateRange', { ns: 'common' })}
                    weekText={t('week', { ns: 'common' })}
                    width={width + 'px'}
                    calendarPlacement="bottom"
                  />
                </div>
              </ExtranetContainer>

              <ExtranetContainer
                full
                marginMediumTop
              >
                <PharmacButton
                  variant="secondary"
                  disabled={!formhook.formState.isValid || !isValidDateRange()}
                  onClick={formhook.handleSubmit(() => getReportData(formhook.getValues()))}
                  style={{ minWidth: '100%' }}
                >
                  <PharmacIcon icon="fileText" />
                  <PharmacText marginSmallLeft>{t('customer.reports.formReport')}</PharmacText>
                </PharmacButton>
              </ExtranetContainer>
            </ExtranetContainer>
          )}
        </FormProvider>
        <ReportLoadingModal showOnProp={reportPending} />
      </>
    )
  }

  return (
    <ExtranetWrapper>
      <ExtranetContainer
        full
        paddingMediumBottom
        paddingSmallHorizontal={!minDesktop}
      >
        <ExtranetContainer
          full
          flexColumn
          alignCenter={minDesktop}
          paddingLargeVertical={minDesktop}
          paddingLargeTop={!minDesktop}
          paddingMediumBottom={!minDesktop}
        >
          <ExtranetContainer
            full
            marginLargeBottom
          >
            <PharmacH4
              style={{ color: 'var(--color-primary)', overflowWrap: 'anywhere' }}
              paddingMediumRight
            >
              {t('customer.reports.reports')}
            </PharmacH4>
          </ExtranetContainer>

          <ExtranetContainer
            full
            flexColumn
          >
            <ReportOptionsContainer>
              <ReportsOptions />
            </ReportOptionsContainer>
          </ExtranetContainer>
        </ExtranetContainer>
      </ExtranetContainer>
    </ExtranetWrapper>
  )
}
